import { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Input, Tag, Empty } from 'antd'
import type { InputRef } from 'antd'
import {
  SearchOutlined,
  AppstoreOutlined,
  UserOutlined,
  RocketOutlined,
  AimOutlined,
  CompassOutlined,
  AlertOutlined,
  CloudOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { PILOTS, AIRCRAFT, FLIGHT_PLANS, TARGETS, RADAR_SOURCES, AIRPORTS, FLIGHT_ROUTES, AIRSPACES, ALERTS } from '@/mocks'

type ResultGroup = 'page' | 'plan' | 'pilot' | 'aircraft' | 'target' | 'radar' | 'airport' | 'route' | 'airspace' | 'alert'

interface SearchResult {
  group: ResultGroup
  id: string
  title: string
  sub: string
  path: string
  icon: React.ReactNode
}

const GROUP_LABEL: Record<ResultGroup, string> = {
  page: 'Trang',
  plan: 'Kế hoạch bay',
  pilot: 'Phi công',
  aircraft: 'Máy bay',
  target: 'Mục tiêu',
  radar: 'Trạm radar',
  airport: 'Sân bay',
  route: 'Đường bay',
  airspace: 'Không vực',
  alert: 'Cảnh báo',
}

const GROUP_COLOR: Record<ResultGroup, string> = {
  page: 'default',
  plan: 'blue',
  pilot: 'cyan',
  aircraft: 'geekblue',
  target: 'red',
  radar: 'volcano',
  airport: 'green',
  route: 'gold',
  airspace: 'purple',
  alert: 'orange',
}

const PAGES: SearchResult[] = [
  { group: 'page', id: 'p-dash', title: 'Trang chủ — Dashboard', sub: '/dashboard', path: '/dashboard', icon: <AppstoreOutlined /> },
  { group: 'page', id: 'p-sch', title: 'Sở chỉ huy', sub: '/sch', path: '/sch', icon: <AimOutlined /> },
  { group: 'page', id: 'p-tc', title: 'Tác chiến — Trực ban', sub: '/tac-chien', path: '/tac-chien', icon: <AimOutlined /> },
  { group: 'page', id: 'p-tc-log', title: 'Tác chiến — Nhật ký trực ban', sub: '/tac-chien/logbook', path: '/tac-chien/logbook', icon: <AimOutlined /> },
  { group: 'page', id: 'p-tc-sar', title: 'Tác chiến — Tìm kiếm cứu nạn', sub: '/tac-chien/sar', path: '/tac-chien/sar', icon: <AimOutlined /> },
  { group: 'page', id: 'p-tc-train', title: 'Tác chiến — Huấn luyện', sub: '/tac-chien/training', path: '/tac-chien/training', icon: <AimOutlined /> },
  { group: 'page', id: 'p-tc-safe', title: 'Tác chiến — An toàn bay', sub: '/tac-chien/safety', path: '/tac-chien/safety', icon: <AimOutlined /> },
  { group: 'page', id: 'p-tc-recon', title: 'Tác chiến — Trinh sát', sub: '/tac-chien/recon', path: '/tac-chien/recon', icon: <AimOutlined /> },
  { group: 'page', id: 'p-tc-map', title: 'Tác chiến — Bản đồ tactical', sub: '/tac-chien/map', path: '/tac-chien/map', icon: <AimOutlined /> },
  { group: 'page', id: 'p-khb', title: 'Kế hoạch bay — Danh sách', sub: '/ke-hoach-bay/plans', path: '/ke-hoach-bay/plans', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-khb-cal', title: 'Kế hoạch bay — Lịch & Gantt', sub: '/ke-hoach-bay/calendar', path: '/ke-hoach-bay/calendar', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-khb-new', title: 'Kế hoạch bay — Lập KHB mới', sub: '/ke-hoach-bay/plans/new', path: '/ke-hoach-bay/plans/new', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-khb-wf', title: 'Kế hoạch bay — Workflow phê duyệt', sub: '/ke-hoach-bay/workflow', path: '/ke-hoach-bay/workflow', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-khb-pilots', title: 'Kế hoạch bay — Phi công', sub: '/ke-hoach-bay/pilots', path: '/ke-hoach-bay/pilots', icon: <UserOutlined /> },
  { group: 'page', id: 'p-khb-ac', title: 'Kế hoạch bay — Máy bay', sub: '/ke-hoach-bay/aircraft', path: '/ke-hoach-bay/aircraft', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-khb-as', title: 'Kế hoạch bay — Không vực 3D', sub: '/ke-hoach-bay/airspace', path: '/ke-hoach-bay/airspace', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-khb-co', title: 'Kế hoạch bay — Hiệp đồng dân dụng', sub: '/ke-hoach-bay/coordination', path: '/ke-hoach-bay/coordination', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-khb-b2', title: 'Kế hoạch bay — Phát B2', sub: '/ke-hoach-bay/distribution', path: '/ke-hoach-bay/distribution', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-dd', title: 'Dẫn đường — Bản đồ', sub: '/dan-duong', path: '/dan-duong', icon: <CompassOutlined /> },
  { group: 'page', id: 'p-dd-comp', title: 'Dẫn đường — Tính toán đường bay', sub: '/dan-duong/compute', path: '/dan-duong/compute', icon: <CompassOutlined /> },
  { group: 'page', id: 'p-dd-dec', title: 'Dẫn đường — Hỗ trợ ra QĐ', sub: '/dan-duong/decision', path: '/dan-duong/decision', icon: <CompassOutlined /> },
  { group: 'page', id: 'p-dd-w', title: 'Dẫn đường — Phương án vũ khí', sub: '/dan-duong/weapons', path: '/dan-duong/weapons', icon: <CompassOutlined /> },
  { group: 'page', id: 'p-dd-perf', title: 'Dẫn đường — Hiệu suất chiến đấu', sub: '/dan-duong/performance', path: '/dan-duong/performance', icon: <CompassOutlined /> },
  { group: 'page', id: 'p-vt', title: 'Vận tải — Kế hoạch', sub: '/van-tai', path: '/van-tai', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-vt-pax', title: 'Vận tải — Hành khách', sub: '/van-tai/pax', path: '/van-tai/pax', icon: <UserOutlined /> },
  { group: 'page', id: 'p-vt-cargo', title: 'Vận tải — Hàng hóa', sub: '/van-tai/cargo', path: '/van-tai/cargo', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-vt-cam', title: 'Vận tải — Camera', sub: '/van-tai/camera', path: '/van-tai/camera', icon: <RocketOutlined /> },
  { group: 'page', id: 'p-kt-map', title: 'Khí tượng — Bản đồ đa lớp', sub: '/khi-tuong/map', path: '/khi-tuong/map', icon: <CloudOutlined /> },
  { group: 'page', id: 'p-kt-st', title: 'Khí tượng — Trạm', sub: '/khi-tuong/stations', path: '/khi-tuong/stations', icon: <CloudOutlined /> },
  { group: 'page', id: 'p-kt-pi', title: 'Khí tượng — Pipeline', sub: '/khi-tuong/pipeline', path: '/khi-tuong/pipeline', icon: <CloudOutlined /> },
  { group: 'page', id: 'p-kt-al', title: 'Khí tượng — Cảnh báo', sub: '/khi-tuong/alerts', path: '/khi-tuong/alerts', icon: <CloudOutlined /> },
  { group: 'page', id: 'p-kt-mt', title: 'Khí tượng — METAR/TAF', sub: '/khi-tuong/metar-taf', path: '/khi-tuong/metar-taf', icon: <CloudOutlined /> },
  { group: 'page', id: 'p-bd', title: 'Báo động — Tổng quan', sub: '/bao-dong', path: '/bao-dong', icon: <AlertOutlined /> },
  { group: 'page', id: 'p-bd-tg', title: 'Báo động — Mục tiêu', sub: '/bao-dong/targets', path: '/bao-dong/targets', icon: <AlertOutlined /> },
  { group: 'page', id: 'p-bd-al', title: 'Báo động — Cảnh báo', sub: '/bao-dong/alerts', path: '/bao-dong/alerts', icon: <AlertOutlined /> },
  { group: 'page', id: 'p-bd-map', title: 'Báo động — Bản đồ tactical', sub: '/bao-dong/map', path: '/bao-dong/map', icon: <AlertOutlined /> },
  { group: 'page', id: 'p-bd-rad', title: 'Báo động — Trạm radar', sub: '/bao-dong/sources', path: '/bao-dong/sources', icon: <AlertOutlined /> },
  { group: 'page', id: 'p-bd-sch', title: 'Báo động — 53 SCH', sub: '/bao-dong/sch', path: '/bao-dong/sch', icon: <AlertOutlined /> },
  { group: 'page', id: 'p-bd-sop', title: 'Báo động — SOP', sub: '/bao-dong/sop', path: '/bao-dong/sop', icon: <AlertOutlined /> },
  { group: 'page', id: 'p-ad', title: 'Admin — Đồng bộ BQP', sub: '/admin', path: '/admin', icon: <AppstoreOutlined /> },
  { group: 'page', id: 'p-ad-org', title: 'Admin — Sơ đồ tổ chức', sub: '/admin/org', path: '/admin/org', icon: <AppstoreOutlined /> },
  { group: 'page', id: 'p-ad-inv', title: 'Admin — Inventory phần mềm', sub: '/admin/inventory', path: '/admin/inventory', icon: <AppstoreOutlined /> },
  { group: 'page', id: 'p-ad-infra', title: 'Admin — Hạ tầng', sub: '/admin/infra', path: '/admin/infra', icon: <AppstoreOutlined /> },
  { group: 'page', id: 'p-ad-cat', title: 'Admin — Data Catalog', sub: '/admin/catalog', path: '/admin/catalog', icon: <AppstoreOutlined /> },
  { group: 'page', id: 'p-ad-comp', title: 'Admin — Compliance', sub: '/admin/compliance', path: '/admin/compliance', icon: <AppstoreOutlined /> },
  { group: 'page', id: 'p-ad-rbac', title: 'Admin — Phân quyền RBAC', sub: '/admin/rbac', path: '/admin/rbac', icon: <AppstoreOutlined /> },
  { group: 'page', id: 'p-ad-au', title: 'Admin — Audit log', sub: '/admin/audit', path: '/admin/audit', icon: <AppstoreOutlined /> },
]

function buildIndex(): SearchResult[] {
  const list: SearchResult[] = [...PAGES]

  for (const p of FLIGHT_PLANS.slice(0, 60)) {
    list.push({
      group: 'plan',
      id: p.id,
      title: `KHB ${p.code}`,
      sub: `${Array.from(new Set(p.sortiePurposes)).join('/')} · ${p.aircraftIds[0] ?? '—'} · ${p.status}`,
      path: `/ke-hoach-bay/plans/${p.id}`,
      icon: <RocketOutlined />,
    })
  }
  for (const pi of PILOTS) {
    list.push({
      group: 'pilot',
      id: pi.id,
      title: `${pi.rank} ${pi.fullName}`,
      sub: `${pi.pilotClass} · ${pi.totalHours.toFixed(0)}h · ${pi.aircraftTypes.join(', ')}`,
      path: `/ke-hoach-bay/pilots/${pi.id}`,
      icon: <UserOutlined />,
    })
  }
  for (const ac of AIRCRAFT) {
    list.push({
      group: 'aircraft',
      id: ac.id,
      title: `${ac.tailNumber} — ${ac.type}`,
      sub: `${ac.category} · ${ac.totalHours.toFixed(0)}h · ${ac.status}`,
      path: `/ke-hoach-bay/aircraft/${ac.id}`,
      icon: <RocketOutlined />,
    })
  }
  for (const t of TARGETS.slice(0, 80)) {
    list.push({
      group: 'target',
      id: t.id,
      title: `Mục tiêu ${t.trackId}`,
      sub: `${t.classification} · ${t.category} · ${Math.round(t.position.alt)}m`,
      path: `/bao-dong/targets/${t.id}`,
      icon: <AimOutlined />,
    })
  }
  for (const r of RADAR_SOURCES) {
    list.push({
      group: 'radar',
      id: r.id,
      title: `Radar ${r.code} — ${r.name}`,
      sub: `${r.type} · ${r.rangeKm}km · ${r.status}`,
      path: '/bao-dong/sources',
      icon: <AlertOutlined />,
    })
  }
  for (const a of AIRPORTS) {
    list.push({
      group: 'airport',
      id: a.id,
      title: `${a.code} — ${a.name}`,
      sub: `${a.icao ?? '—'} · ${a.runways} runway`,
      path: '/ke-hoach-bay/coordination',
      icon: <RocketOutlined />,
    })
  }
  for (const r of FLIGHT_ROUTES) {
    list.push({
      group: 'route',
      id: r.id,
      title: `Đường bay ${r.code}`,
      sub: `${r.type} · ${r.waypoints?.length ?? 0} waypoint · ${Math.round(r.distanceKm)}km`,
      path: '/dan-duong/compute',
      icon: <CompassOutlined />,
    })
  }
  for (const z of AIRSPACES) {
    list.push({
      group: 'airspace',
      id: z.id,
      title: `Không vực ${z.code} — ${z.name}`,
      sub: `${z.type} · ${z.altitudeMin}-${z.altitudeMax}m`,
      path: '/ke-hoach-bay/airspace',
      icon: <RocketOutlined />,
    })
  }
  for (const al of ALERTS.slice(0, 35)) {
    list.push({
      group: 'alert',
      id: al.id,
      title: `Cảnh báo ${al.ruleCode}`,
      sub: `${al.severity} · ${al.ruleName}`,
      path: '/bao-dong/alerts',
      icon: <AlertOutlined />,
    })
  }
  return list
}

const INDEX = buildIndex()

function fuzzyScore(text: string, q: string): number {
  if (!q) return 0
  const t = text.toLowerCase()
  const ql = q.toLowerCase()
  if (t === ql) return 1000
  if (t.startsWith(ql)) return 500
  if (t.includes(ql)) return 200
  let ti = 0
  let score = 0
  for (let i = 0; i < ql.length; i++) {
    const idx = t.indexOf(ql[i], ti)
    if (idx === -1) return 0
    score += 10 - Math.min(9, idx - ti)
    ti = idx + 1
  }
  return score
}

interface GlobalSearchProps {
  open: boolean
  onClose: () => void
}

export function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const navigate = useNavigate()
  const inputRef = useRef<InputRef>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setQuery('')
      setHighlight(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  const results = useMemo(() => {
    if (!query.trim()) {
      // default: show pages first
      return PAGES.slice(0, 12)
    }
    const scored: { r: SearchResult; s: number }[] = []
    for (const r of INDEX) {
      const s = Math.max(fuzzyScore(r.title, query), fuzzyScore(r.sub, query) * 0.5)
      if (s > 0) scored.push({ r, s })
    }
    scored.sort((a, b) => b.s - a.s)
    return scored.slice(0, 30).map((x) => x.r)
  }, [query])

  useEffect(() => {
    setHighlight(0)
  }, [query])

  function go(r: SearchResult) {
    navigate(r.path)
    onClose()
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(results.length - 1, h + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(0, h - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const r = results[highlight]
      if (r) go(r)
    }
  }

  // Group results for display
  const grouped = useMemo(() => {
    const m = new Map<ResultGroup, SearchResult[]>()
    for (const r of results) {
      const list = m.get(r.group) ?? []
      list.push(r)
      m.set(r.group, list)
    }
    return Array.from(m.entries())
  }, [results])

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={680}
      closable={false}
      destroyOnHidden
      styles={{ body: { padding: 0 } }}
      className="!top-[10vh]"
    >
      <div className="border-b border-[var(--color-border-subtle)] px-3 py-2 flex items-center gap-2">
        <SearchOutlined className="text-[var(--color-text-muted)]" />
        <Input
          ref={inputRef}
          variant="borderless"
          placeholder="Tìm trang, KHB, phi công, máy bay, mục tiêu..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKey}
          className="!text-[14px]"
        />
        <span className="font-mono text-[10px] text-[var(--color-text-muted)] border border-[var(--color-border-subtle)] rounded px-1.5 py-0.5">ESC</span>
      </div>

      <div ref={listRef} className="max-h-[60vh] overflow-y-auto p-1">
        {results.length === 0 ? (
          <div className="py-8">
            <Empty description="Không có kết quả" />
          </div>
        ) : (
          (() => {
            let runningIndex = 0
            return grouped.map(([group, rows]) => (
              <div key={group} className="mb-2">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                  {GROUP_LABEL[group]} ({rows.length})
                </div>
                {rows.map((r) => {
                  const isHi = runningIndex === highlight
                  runningIndex++
                  return (
                    <button
                      key={r.id}
                      onClick={() => go(r)}
                      onMouseEnter={() => setHighlight(runningIndex - 1)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded text-left transition cursor-pointer ${
                        isHi ? 'bg-[var(--color-primary-500)]/15' : 'hover:bg-[var(--color-bg-elevated)]'
                      }`}
                    >
                      <span className="text-[var(--color-text-muted)] text-base">{r.icon}</span>
                      <span className="flex-1 min-w-0">
                        <div className="text-[13px] text-[var(--color-text-primary)] truncate">{r.title}</div>
                        <div className="text-[11px] text-[var(--color-text-muted)] truncate">{r.sub}</div>
                      </span>
                      <Tag color={GROUP_COLOR[r.group]} className="!m-0 !text-[10px]">{GROUP_LABEL[r.group]}</Tag>
                      <RightOutlined className="text-[var(--color-text-muted)] text-[10px]" />
                    </button>
                  )
                })}
              </div>
            ))
          })()
        )}
      </div>

      <div className="border-t border-[var(--color-border-subtle)] px-3 py-1.5 flex items-center gap-3 text-[10px] text-[var(--color-text-muted)]">
        <span>↑↓ điều hướng</span>
        <span>↵ chọn</span>
        <span>esc đóng</span>
        <span className="ml-auto">Index: {INDEX.length} mục</span>
      </div>
    </Modal>
  )
}
